import { Alert, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './widgets.$id.embed.module.css'
import { api, API_BASE_URL } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/widgets/$id/embed')({
  component: WidgetEmbed,
})

function WidgetEmbed() {
  const { id } = Route.useParams()

  const [embedCode, setEmbedCode] = useState<string | null>(null)
  const [widgetName, setWidgetName] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const [embedResponse, widgetResponse] = await Promise.all([
          api.getWidgetEmbedCode(id),
          api.getWidget(id),
        ])
        setEmbedCode(embedResponse.data.embedCode)
        setWidgetName(widgetResponse.data.name)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load embed code')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchData()
  }, [id])

  const handleCopyEmbed = () => {
    if (!embedCode) return
    void navigator.clipboard.writeText(embedCode)
    toast.success('Embed code copied to clipboard')
  }

  const reactCode = `import { useEffect } from 'react'

function ProofPulseWidget() {
  useEffect(() => {
    const script = document.createElement('script')
    script.src = '${API_BASE_URL}/widgets/${id}/script.js'
    script.async = true
    document.body.appendChild(script)
    return () => { document.body.removeChild(script) }
  }, [])

  return <div id="proofpulse-widget-${id}" />
}`

  const handleCopyReact = () => {
    void navigator.clipboard.writeText(reactCode)
    toast.success('React code copied to clipboard')
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
        <p>Loading embed code...</p>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>
  }

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        Copy the embed code below and paste it into your website to display the{' '}
        <strong>{widgetName}</strong> widget.
      </p>

      {/* Live Preview */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Live Preview</h3>
        <p className={styles.sectionDescription}>
          This is how your widget will appear on your website.
        </p>
        <div className={styles.previewContainer}>
          <iframe
            src={`${API_BASE_URL}/widgets/${id}/preview`}
            className={styles.previewFrame}
            title="Widget Preview"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>

      {/* HTML Embed */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>HTML Script Tag</h3>
        <p className={styles.sectionDescription}>
          Paste this code snippet into your HTML where you want the widget to appear.
        </p>
        <Card>
          <CardBody>
            <div className={styles.codeWrapper}>
              <div className={styles.codeBlock}>
                <code className={styles.code}>
                  {embedCode ??
                    `<script src="${API_BASE_URL}/widgets/${id}/script.js" async></script>\n<div id="proofpulse-widget-${id}"></div>`}
                </code>
              </div>
              <div className={styles.copyBtn}>
                <Button size="sm" variant="primary" onClick={handleCopyEmbed}>
                  Copy
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* React Component */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>React Component</h3>
        <p className={styles.sectionDescription}>
          Use this component in your React application to embed the widget.
        </p>
        <Card>
          <CardBody>
            <div className={styles.codeWrapper}>
              <div className={styles.codeBlock}>
                <code className={styles.code}>{reactCode}</code>
              </div>
              <div className={styles.copyBtn}>
                <Button size="sm" variant="primary" onClick={handleCopyReact}>
                  Copy
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Instructions */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Instructions</h3>
        <Card>
          <CardBody>
            <ol className={styles.instructions}>
              <li>Copy the embed code above (HTML or React).</li>
              <li>Paste it into the page where you want testimonials to appear.</li>
              <li>The widget will automatically load and display your approved testimonials.</li>
              <li>
                Changes to widget settings are reflected immediately -- no code changes needed.
              </li>
              <li>
                Only <code>approved</code> testimonials are shown in the widget.
              </li>
            </ol>
          </CardBody>
        </Card>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Link to="/widgets/$id/edit" params={{ id }}>
          <Button variant="secondary">Edit Widget</Button>
        </Link>
        <Link to="/widgets">
          <Button variant="ghost">Back to Widgets</Button>
        </Link>
      </div>
    </div>
  )
}
